import { useQuery, useTeam } from '@vrplatform/data';
import { type Maybe, day, ensure } from '@vrplatform/utils';
import { useDebouncedValue } from '@mantine/hooks';
import type {
  listing_owner_role_enum,
  owner_statement,
} from '@vrplatform/graphql';
import {
  formatOwnerName,
  formatUserName,
  getListingAddress,
  getListingDisabledAutomations,
  getListingName,
} from '@vrplatform/ui-common';
import { getListingDeactivation } from '../detail/useListingDetailViewQuery';

export function useListingLegacyDetailDrawerQuery(id: Maybe<string>) {
  const [{ id: teamId }] = useTeam();

  const query = useQuery(
    (q, args) => {
      if (!args.id)
        return {
          listing: null,
        };

      return q
        .listings({
          where: {
            id: { _eq: args.id },
          },
        })
        .map((listing) => {
          const { disabledAutomations } =
            getListingDisabledAutomations(listing);

          const listingConnections = listing
            .connections({
              order_by: [
                {
                  name: 'asc_nulls_last',
                },
              ],
            })
            .map((listingConnection) => ({
              id: listingConnection.id,
              name: listingConnection.name,
              icon: listingConnection.connection.app.iconRound,
              app: listingConnection.connection.app.name,
            }));

          const getStatement = (st: owner_statement) => ({
            id: st.id,
            periodId: st.listingOwnershipPeriodId,
            startAt: st.startAt,
            status: st.status,
            owners: st.members().map((member) => ({
              id: member.userId,
              name: formatUserName(member.user),
            })),
          });

          return {
            listing: {
              id: listing.id,
              address: getListingAddress(listing),
              addressId: listing.address_id,
              status: listing.status,
              deactivation: getListingDeactivation(listing, args.today),
              sourceStatus: listing.source?.status,
              currency: listing.defaultCurrency,
              pmsStatus: listing.pmsStatus,
              name: getListingName(listing),
              collection: {
                id: listing.collectionId,
                name: listing.collection?.name || '',
              },
              pmsName: listing.name,
              title: listing.title,
              disabledAutomations,
              tenantId: teamId,
              isPmsListing: !!listing.connectionId,
              ownerships: listing
                .ownerships({
                  where: {
                    newOwnerId: {
                      _is_null: false,
                    },
                  },
                  order_by: [
                    {
                      createdAt: 'asc_nulls_last',
                    },
                  ],
                })
                .map((ownership) => {
                  return {
                    id: ownership.id,
                    split: ownership.split,
                    role: ensure<listing_owner_role_enum>(
                      ownership.newOwner?.type === 'company'
                        ? 'company'
                        : 'owner'
                    ),
                    owner: {
                      id: ownership.newOwner?.id,
                      type: ownership.newOwner?.type,
                      name: formatOwnerName(ownership.newOwner),
                    },
                  };
                })
                .filter((x) => x.owner.id),
              ownerStatements: listing
                .ownerStatements({
                  order_by: [
                    {
                      startAt: 'desc_nulls_last',
                    },
                  ],
                })
                .map(getStatement),
              listingConnections,
            },
          };
        })[0];
    },
    {
      skip: !id,
      queryKey: 'listings',
      variables: {
        id,
        teamId,
        today: day().yyyymmdd(),
      },
    }
  );

  const [debounced] = useDebouncedValue(query.data, 500);

  return { ...query, data: query.data || debounced };
}

export type Listing = NonNullable<
  NonNullable<
    ReturnType<typeof useListingLegacyDetailDrawerQuery>['data']
  >['listing']
>;
